import type { ReactElement } from "react";
import { useCallback, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { EmptyState } from "@similarweb/ui-components";

import { useUnmount } from "app/custom-hooks/useUnmount";
import { translate } from "app/filters/i18nFilter";
import { DemoModeBanner } from "app/pages/sales-intelligence/common-components/demo-mode/DemoModeBanner/DemoModeBanner";
import { ErrorBoundaryWithLogging } from "app/pages/sales-intelligence/common-components/ErrorBoundaryWithLogging";
import { TabbedPageContent } from "app/pages/sales-intelligence/common-components/tabbed-page-content";
import trackingService from "app/pages/sales-intelligence/common-components/tabbed-page-content/services/tracking/trackingService";
import type { PageTabBase } from "app/pages/sales-intelligence/common-components/tabbed-page-content/types";
import { resetFilteringStateAction } from "app/pages/sales-intelligence/modules/advanced-search-filtering/store/action-creators";
import { isLeadsType, setActiveLeadsType } from "app/pages/sales-intelligence/modules/leads-search";
import useTabsData from "app/pages/sales-intelligence/pages/opportunity-list/hooks/useTabsData";
import { setActivePageTab } from "app/pages/sales-intelligence/sub-modules/common/store/action-creators";
import { selectActivePageTab } from "app/pages/sales-intelligence/sub-modules/common/store/selectors";

import { buildTabsConfig } from "../../configuration/tabsConfig";
import type { PageTabId } from "../../types/page-tabs";

const PageMainContent = (): ReactElement => {
    const dispatch = useDispatch();
    const activeTab = useSelector(selectActivePageTab);

    const tabsConfig = useMemo(() => buildTabsConfig(), []);

    useTabsData(tabsConfig);

    useUnmount(() => {
        dispatch(resetFilteringStateAction());
        dispatch(setActivePageTab(null));
    });

    const handleTabSelect = useCallback((tabId: PageTabId): void => {
        if (isLeadsType(tabId)) {
            dispatch(setActiveLeadsType(tabId));
        }
        dispatch(setActivePageTab(tabId));
    }, []);

    const handleTabClick = useCallback((tabId: PageTabId): void => {
        trackingService.trackSelectTab(tabId);
    }, []);

    return (
        <TabbedPageContent<PageTabId, PageTabBase<PageTabId>>
            tabs={tabsConfig}
            selectedTabId={activeTab}
            belowTabsSlot={<DemoModeBanner />}
            onTabSelect={handleTabSelect}
            onTabClick={handleTabClick}
        />
    );
};

const PageMainContentWithErrorBoundary = () => (
    <ErrorBoundaryWithLogging
        fallback={
            <EmptyState
                title={translate("error.boundary.title")}
                iconName="no-data-tree"
            />
        }
    >
        <PageMainContent />
    </ErrorBoundaryWithLogging>
);

export default PageMainContentWithErrorBoundary;
